/**
 * displayMode.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Manages the note display mode (Visible vs Discreet) for MarginPro.
 *
 * Settings are stored per-document in a Word Custom XML part so all
 * collaborators who open the same document inherit the same display style.
 *
 * Icon preference is stored per-user in Firestore (personal) or per-org-admin
 * in Firestore (enterprise) — handled at the component level.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type DisplayMode = 'visible' | 'discreet';

export interface NoteDisplayOverride {
  mode: DisplayMode;
  iconId: string;
}

export interface DocumentDisplaySettings {
  mode: DisplayMode;
  iconId: string;
  /** noteId → override */
  overrides: Record<string, NoteDisplayOverride>;
}

// ─── Classic Icons ────────────────────────────────────────────────────────────

export interface ClassicIcon {
  id: string;
  char: string;
  label: string;
  /** True for the MarginPro M logomark — rendered with special styling */
  isLogo?: boolean;
}

export const CLASSIC_ICONS: ClassicIcon[] = [
  { id: 'diamond',    char: '◆', label: 'Diamond' },
  { id: 'circle',     char: '●', label: 'Circle' },
  { id: 'square',     char: '■', label: 'Square' },
  { id: 'arrow',      char: '▸', label: 'Arrow' },
  { id: 'chevron',    char: '›', label: 'Chevron' },
  { id: 'star',       char: '★', label: 'Star' },
  { id: 'sparkle',    char: '✦', label: 'Sparkle' },
  { id: 'asterisk',   char: '✱', label: 'Asterisk' },
  { id: 'flag',       char: '⚑', label: 'Flag' },
  { id: 'bookmark',   char: '❧', label: 'Bookmark' },
  { id: 'pencil',     char: '✎', label: 'Pencil' },
  { id: 'pin',        char: '📌', label: 'Pin' },
  { id: 'tag',        char: '🏷', label: 'Tag' },
  { id: 'lightning',  char: '⚡', label: 'Lightning' },
  { id: 'check',      char: '✔', label: 'Check' },
  { id: 'info',       char: 'ℹ', label: 'Info' },
  { id: 'dot',        char: '•', label: 'Bullet' },
  { id: 'dagger',     char: '†', label: 'Dagger' },
  { id: 'marginpro',  char: 'M', label: 'Margin Pro', isLogo: true },
];

export const DEFAULT_ICON_ID = 'diamond';

export const DISPLAY_DEFAULTS: DocumentDisplaySettings = {
  mode: 'visible',
  iconId: DEFAULT_ICON_ID,
  overrides: {},
};

// ─── Custom XML ───────────────────────────────────────────────────────────────

const DISPLAY_XML_NAMESPACE = 'http://schemas.marginpro.com/display';

function buildXml(settings: DocumentDisplaySettings): string {
  const overrideElements = Object.entries(settings.overrides)
    .map(([id, o]) => `<Override id="${escapeXml(id)}" mode="${o.mode}" iconId="${escapeXml(o.iconId)}"/>`)
    .join('');
  return (
    `<DisplaySettings xmlns="${DISPLAY_XML_NAMESPACE}">` +
    `<Mode>${settings.mode}</Mode>` +
    `<IconId>${escapeXml(settings.iconId)}</IconId>` +
    `<Overrides>${overrideElements}</Overrides>` +
    `</DisplaySettings>`
  );
}

function parseXml(xml: string): DocumentDisplaySettings {
  try {
    const modeMatch   = xml.match(/<Mode>([^<]+)<\/Mode>/);
    const iconMatch   = xml.match(/<IconId>([^<]*)<\/IconId>/);
    const mode: DisplayMode = modeMatch?.[1] === 'discreet' ? 'discreet' : 'visible';
    const iconId = iconMatch?.[1] || DEFAULT_ICON_ID;

    const overrides: Record<string, NoteDisplayOverride> = {};
    const overrideRe = /<Override\s+id="([^"]+)"\s+mode="([^"]+)"\s+iconId="([^"]*)"/g;
    let m: RegExpExecArray | null;
    while ((m = overrideRe.exec(xml)) !== null) {
      const [, id, oMode, oIconId] = m;
      overrides[id] = {
        mode: oMode === 'discreet' ? 'discreet' : 'visible',
        iconId: oIconId || DEFAULT_ICON_ID,
      };
    }
    return { mode, iconId, overrides };
  } catch {
    return { ...DISPLAY_DEFAULTS };
  }
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ─── Read / Write ─────────────────────────────────────────────────────────────

export async function getDocumentDisplaySettings(): Promise<DocumentDisplaySettings> {
  if (typeof Office === 'undefined' || !Office?.context?.document) {
    return { ...DISPLAY_DEFAULTS };
  }
  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(DISPLAY_XML_NAMESPACE);
      context.load(parts, 'items');
      await context.sync();
      if (parts.items.length === 0) return { ...DISPLAY_DEFAULTS, overrides: {} };
      const xmlResult = parts.items[0].getXml();
      await context.sync();
      return parseXml(xmlResult.value);
    });
  } catch {
    return { ...DISPLAY_DEFAULTS };
  }
}

export async function saveDocumentDisplaySettings(settings: DocumentDisplaySettings): Promise<void> {
  if (typeof Office === 'undefined' || !Office?.context?.document) return;
  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(DISPLAY_XML_NAMESPACE);
      context.load(parts, 'items');
      await context.sync();
      if (parts.items.length > 0) parts.items[0].delete();
      context.document.customXmlParts.add(buildXml(settings));
      await context.sync();
    });
  } catch (err) {
    console.error('[DisplayMode] Failed to save settings:', err);
  }
}

// ─── Icon helpers ─────────────────────────────────────────────────────────────

export function getIconById(iconId: string): ClassicIcon {
  return CLASSIC_ICONS.find(i => i.id === iconId) ?? CLASSIC_ICONS[0];
}

/**
 * Builds the HTML string to inject into a content control for discreet mode.
 *
 * @param label      The note label text
 * @param icon       The resolved ClassicIcon object
 * @param fontName   Surrounding font name (from cc.tag snapshot)
 * @param fontSize   Surrounding font size in pt (from cc.tag snapshot)
 * @param fontColor  Surrounding font color hex (from cc.tag snapshot)
 * @param fontBold   Surrounding bold state (from cc.tag snapshot)
 * @param fontItalic Surrounding italic state (from cc.tag snapshot)
 */
export function buildDiscreetHtml(
  label: string,
  icon: ClassicIcon,
  fontName: string,
  fontSize: number,
  fontColor: string,
  fontBold: boolean,
  fontItalic: boolean,
  /** When present, wraps the content in an <a href> for appendix navigation */
  hrefTarget?: string,
): string {
  const safeLabel = escapeXml(label);
  const resolvedColor = fontColor && fontColor !== 'null' && fontColor !== '' ? fontColor : '#000000';
  const resolvedSize = isNaN(fontSize) || fontSize <= 0 ? 12 : fontSize;
  const labelStyle = [
    fontName && fontName !== 'null' ? `font-family: '${fontName}'` : '',
    `font-size: ${resolvedSize}pt`,
    `color: ${resolvedColor}`,
    fontBold  ? 'font-weight: bold'   : 'font-weight: normal',
    fontItalic ? 'font-style: italic' : 'font-style: normal',
    'text-decoration: none',
  ].filter(Boolean).join('; ');

  const iconSize = icon.isLogo
    ? Math.max(10, Math.round(resolvedSize * 0.80))
    : Math.max(8,  Math.round(resolvedSize * 0.75));
  const iconStyle = icon.isLogo
    ? `font-size: ${iconSize}pt; color: #2f8570; font-weight: 900; font-family: 'Segoe UI', Arial, sans-serif; text-decoration: none;`
    : `font-size: ${iconSize}pt; color: #2f8570; text-decoration: none;`;
  const iconHtml = `<span style="${iconStyle}">${icon.char}</span>`;

  const inner = `<span style="${labelStyle}">${safeLabel}</span>${iconHtml}`;

  // When an appendix href is provided, wrap in a transparent anchor for navigation.
  // Inner spans' explicit colors override the anchor's color, preserving discreet appearance.
  if (hrefTarget) {
    // Use the explicit label color (not inherit) so Word's FollowedHyperlink character
    // style cannot override it and turn the text purple after clicking.
    return `<a href="${hrefTarget}" style="text-decoration: none; color: ${resolvedColor};">${inner}</a>`;
  }
  return inner;
}

/**
 * Builds the HTML string for visible mode inside an appendix context.
 * Kept separate so appendix always renders in the styled format.
 */
export function buildAppendixLinkHtml(
  label: string,
  safeId: string,
  fontName: string,
  fontSize: number,
): string {
  const safeLabel = escapeXml(label);
  let styleStr = 'text-decoration: none; color: #2f8570;';
  if (fontName && fontName !== 'null' && fontName !== 'undefined') {
    styleStr += ` font-family: '${fontName}';`;
  }
  if (fontSize && !isNaN(fontSize) && fontSize > 0) {
    styleStr += ` font-size: ${fontSize}pt;`;
  }
  return `<a href="#an_${safeId}" style="${styleStr}">${safeLabel}</a>`;
}
